import { WSAlert, WSList, WSText } from "@wingspanhq/fe-component-library";
import {
  IPayeeResponse,
  IRequirementResponse
} from "@wingspanhq/payments/dist/interfaces";
import { buildPayeeDetailsRequirementsPath } from "../../utils";
import React, { useState } from "react";
import { selectorHasPayoutMethodRequirement } from "../../../Requirements/selectors/selectorHasPayoutMethodRequirement";
import { selectorExpiringRequirements } from "../../../Requirements/selectors/selectorExpiringRequirements";
import { selectorExpiredRequirements } from "../../../Requirements/selectors/selectorExpiredRequirements";
import { UserStatus } from "@wingspanhq/users/dist/lib/interfaces";
import {
  PayeeTaxDocumentSharePermission,
  PayerPayeeStatus
} from "@wingspanhq/payments/dist/interfaces/payerPayee";
import { selectorIsAllRequirementsComplete } from "../../../Requirements/selectors/selectorIsAllRequirementsComplete";
import { useFocusedFeatures } from "../../../../FocusedFeatures";
import { useHistory } from "react-router-dom";
import { IPayeeEngagementResponse } from "@wingspanhq/payments/dist/interfaces/api/payerPayeeEngagement";
import { selectorPayeeHasEligibleEngagements } from "../../selectors/selectorPayeeHasEligibleEngagements";

export const PayeeDetailsAlerts: React.FC<{
  payee: IPayeeResponse;
  requirements: IRequirementResponse[];
  engagements: IPayeeEngagementResponse[];
}> = ({ payee, requirements = [], engagements = [] }) => {
  const history = useHistory();
  const [hideInfo, setHideInfo] = useState<boolean>(false);
  const focusedFeatures = useFocusedFeatures();

  const hasEligibleEngagements =
    selectorPayeeHasEligibleEngagements(engagements);

  const payeeRequirements = payee.requirements || [];
  const isInactive = payee.payerOwnedData.status === PayerPayeeStatus.Inactive;

  const isAllRequirementsComplete = selectorIsAllRequirementsComplete([
    ...requirements,
    ...payeeRequirements
  ]);

  const isNotRegistered = payee.user?.status !== UserStatus.Active;
  const isNotShared =
    payee.payeeOwnedData?.shareTaxDocument !==
    PayeeTaxDocumentSharePermission.Allow;

  const hasPayoutMethodRequirement = selectorHasPayoutMethodRequirement([
    ...requirements
  ]);

  const expiringRequirements = selectorExpiringRequirements(requirements);

  const expiredRequirements = selectorExpiredRequirements(requirements);

  return (
    <WSList gap="L">
      {isNotRegistered && !hideInfo ? (
        <WSAlert
          icon="user"
          onDismiss={() => {
            setHideInfo(prev => !prev);
          }}
          size="M"
          theme="info"
          title="Contractor invited"
        >
          <WSText.ParagraphSm weight="book" color="gray700">
            The contractor has been invited by the email provided to sign-up for
            a Wingspan account in order to receive payments from you.
          </WSText.ParagraphSm>
        </WSAlert>
      ) : isNotShared && !isNotRegistered ? ( // show if registered but not shared
        <WSAlert
          icon="alert"
          size="M"
          theme="warning"
          title="Tax information not shared"
        >
          <WSText.ParagraphSm weight="book" color="gray700">
            The contractor has not consented to share their tax information with
            you. Payments to the contractor will still be processed. They have
            been notified to share their information for tax filing purposes.
          </WSText.ParagraphSm>
        </WSAlert>
      ) : isInactive ? (
        <WSAlert icon="archive" size="M" theme="info" title="Archived">
          <WSText.ParagraphSm weight="book" color="gray700">
            This contractor is currently inactive and cannot be added to new
            engagements or invoices until they are restored. <br />
            Their eligibility will be recalculated if they are restored. Past
            requirement files for this contractor can be viewed here.
          </WSText.ParagraphSm>
        </WSAlert>
      ) : isAllRequirementsComplete && hasEligibleEngagements ? (
        <WSAlert
          theme="success"
          icon="check-circle"
          title="Contractor is all set for now"
        >
          All eligibility requirements completed. Any new requirements will
          appear here.
        </WSAlert>
      ) : isAllRequirementsComplete ? (
        <WSAlert
          theme="warning"
          icon="alert"
          title="Payments eligibility pending"
        >
          The contractor should have at least one active engagement that is
          eligible for payments.
        </WSAlert>
      ) : focusedFeatures.showContractorDetailsTaxOnlyPayerEligibilityWarning ? (
        <WSAlert theme="warning" icon="alert" title="Requirements incomplete">
          The contractor must complete all requirements to receive tax forms.
        </WSAlert>
      ) : expiringRequirements.length > 0 || expiredRequirements.length > 0 ? (
        <>
          {expiredRequirements.length > 0 && (
            <WSAlert
              theme="error"
              icon="alert-circle"
              title="Requirements expired"
              action={{
                text: "View requirements",
                onClick: () => {
                  history.push(
                    buildPayeeDetailsRequirementsPath(payee.payeeId)
                  );
                }
              }}
            >
              The contractor has {expiredRequirements.length} requirements that
              have expired and require renewal.
            </WSAlert>
          )}

          {expiringRequirements.length > 0 && (
            <WSAlert
              theme="neutral"
              icon="calendar"
              title="Requirements expiring soon"
              action={{
                text: "View requirements",
                onClick: () => {
                  history.push(
                    buildPayeeDetailsRequirementsPath(payee.payeeId)
                  );
                }
              }}
            >
              The contractor has {expiringRequirements.length} requirements
              expiring soon.
            </WSAlert>
          )}
        </>
      ) : hasPayoutMethodRequirement ? (
        <WSAlert theme="warning" icon="alert" title="Requirements incomplete">
          The contractor has incomplete requirements that have been assigned to
          them. In order to receive payments for a particular engagement, all
          requirements for that engagement must be complete.
        </WSAlert>
      ) : (
        <WSAlert theme="warning" icon="alert" title="Requirements incomplete">
          The contractor has incomplete requirements that have been assigned to
          them. The contractor must complete all of them to avoid delays in
          working with you.
        </WSAlert>
      )}
    </WSList>
  );
};
